/**
 * Algolia-Powered Predictive Search Autocomplete
 * ----------------------------------------------
 * Replaces Shopify's native predictive_search dropdown with an Algolia-driven
 * one. Why: Shopify's native predictive search has weak tokenization — for
 * example, "5LBS" matches only 1 of 12 5lb-protein products because most
 * variants are titled "5 LB" or "5 lbs" (with space). Algolia tokenizes those
 * cleanly and returns the full set.
 *
 * Activation: sets window.__algoliaAutocompleteActive = true on load. The
 * existing assets/predictive-search.js bails out via guards on that flag,
 * so we don't fight the native handlers.
 *
 * Renders (only when input is non-empty):
 *   - Suggested-query pills (from shopify_products_query_suggestions)
 *   - Product results (from shopify_products)
 *
 * Empty input deliberately renders nothing — the homepage's trending
 * collection serves as the discovery surface, no need to duplicate that
 * inside the search dropdown.
 *
 * Single-result auto-redirect: pressing Enter when only 1 product matches
 * navigates straight to its PDP.
 *
 * Bilingual: Switches to the shopify_products_ar index when the page locale
 * is Arabic, so Arabic shoppers get Arabic titles and descriptions.
 */
(function () {
  'use strict';

  const _isAr =
    document.documentElement.lang === 'ar' ||
    location.pathname.startsWith('/ar/');

  const CONFIG = {
    appId: '18TT9N4CSD',
    apiKey: '13f15ea1fb4b1badcdcb224b64a1fc69',
    productsIndex: _isAr ? 'shopify_products_ar' : 'shopify_products',
    suggestionsIndex: 'shopify_products_query_suggestions',
    productsLimit: 8,
    suggestionsLimit: 5,
    debounceMs: 150,
    moneyFormat: window.AvnzorAlgoliaConfig?.moneyFormat || '{{amount}}',
  };

  // Disable the native predictive-search handlers.
  window.__algoliaAutocompleteActive = true;

  // (trendingCache removed — no longer fetching trending in the dropdown.)

  // ----- Helpers -----

  function isArabic() {
    return (
      document.documentElement.lang === 'ar' ||
      location.pathname.startsWith('/ar/')
    );
  }

  function localePrefix() {
    return isArabic() ? '/ar' : '';
  }

  function productUrl(handle) {
    return localePrefix() + '/products/' + encodeURIComponent(handle);
  }

  function searchUrl(query) {
    return localePrefix() + '/search?q=' + encodeURIComponent(query);
  }

  function escapeHTML(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatMoney(price) {
    // Algolia stores price as a decimal (e.g. 18.95). Render with shop currency format.
    if (price == null || isNaN(price)) return '';
    const amount = Number(price).toFixed(2);
    const amountNoDec = Math.round(Number(price)).toString();
    const amountComma = amount.replace('.', ',');
    return CONFIG.moneyFormat
      .replace(/{{\s*amount\s*}}/g, amount)
      .replace(/{{\s*amount_no_decimals\s*}}/g, amountNoDec)
      .replace(/{{\s*amount_with_comma_separator\s*}}/g, amountComma)
      .replace(/{{\s*amount_no_decimals_with_comma_separator\s*}}/g, amountNoDec);
  }

  function debounce(fn, ms) {
    let t;
    return function () {
      const args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(ctx, args), ms);
    };
  }

  // Normalize Eastern Arabic-Indic digits (٠-٩, U+0660–U+0669) and
  // Persian/Urdu digits (۰-۹, U+06F0–U+06F9) to Western digits (0-9).
  // Why: Arabic shoppers often type "بروتين رطل ٥" but the catalog titles
  // store the size as "5 رطل" (Western digit). Without normalization, "٥"
  // never matches "5" and the query returns 0 hits.
  function normalizeQuery(q) {
    if (!q) return q;
    return q.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
            .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06F0));
  }

  // ----- Algolia query -----

  async function algoliaMultiQuery(requests) {
    const url = 'https://' + CONFIG.appId.toLowerCase() + '-dsn.algolia.net/1/indexes/*/queries';
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'X-Algolia-Application-Id': CONFIG.appId,
        'X-Algolia-API-Key': CONFIG.apiKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ requests }),
    });
    if (!res.ok) throw new Error('Algolia query failed: ' + res.status);
    return res.json();
  }

  async function fetchSearchResults(query) {
    const q = normalizeQuery(query);
    const requests = [
      {
        indexName: CONFIG.productsIndex,
        params:
          'query=' + encodeURIComponent(q) +
          '&hitsPerPage=' + CONFIG.productsLimit +
          '&filters=inventory_available%3Atrue' +
          '&attributesToRetrieve=' + encodeURIComponent(
            ['title', 'handle', 'image', 'product_image', 'price', 'compare_at_price', 'vendor'].join(',')
          ),
      },
      {
        indexName: CONFIG.suggestionsIndex,
        params:
          'query=' + encodeURIComponent(q) +
          '&hitsPerPage=' + CONFIG.suggestionsLimit +
          '&attributesToRetrieve=query',
      },
    ];
    try {
      const out = await algoliaMultiQuery(requests);
      return {
        products: out.results?.[0]?.hits || [],
        suggestions: out.results?.[1]?.hits || [],
      };
    } catch (e) {
      return { products: [], suggestions: [] };
    }
  }

  // (fetchTrending removed — empty-input dropdown intentionally stays blank;
  // homepage carousels handle trending discovery instead.)

  // ----- Render -----

  function renderProductCard(hit) {
    const img = hit.image || hit.product_image || '';
    const hasCompare = hit.compare_at_price && Number(hit.compare_at_price) > Number(hit.price);
    const url = productUrl(hit.handle);
    return (
      '<li class="predictive-search-results__card predictive-search-results__card--product fgp-algolia-card" role="option">' +
        '<a href="' + url + '" class="fgp-algolia-card__link">' +
          (img ? '<img src="' + escapeHTML(img) + '" alt="" loading="lazy" class="fgp-algolia-card__image"/>' :
                 '<span class="fgp-algolia-card__image fgp-algolia-card__image--placeholder"></span>') +
          '<span class="fgp-algolia-card__body">' +
            '<span class="fgp-algolia-card__title">' + escapeHTML(hit.title || '') + '</span>' +
            '<span class="fgp-algolia-card__price-row">' +
              '<span class="fgp-algolia-card__price">' + escapeHTML(formatMoney(hit.price)) + '</span>' +
              (hasCompare ? '<span class="fgp-algolia-card__compare">' + escapeHTML(formatMoney(hit.compare_at_price)) + '</span>' : '') +
            '</span>' +
          '</span>' +
        '</a>' +
      '</li>'
    );
  }

  function renderSuggestionPill(hit) {
    const q = hit.query || '';
    if (!q) return '';
    return (
      '<li class="predictive-search-results__card--query" role="option">' +
        '<a class="pills__pill predictive-search-results__pill fgp-algolia-pill" href="' + searchUrl(q) + '">' +
          escapeHTML(q) +
        '</a>' +
      '</li>'
    );
  }

  function renderEmpty(query) {
    return (
      '<div class="fgp-algolia-empty">' +
        '<p class="predictive-search-results__no-results">' +
          'No results for "' + escapeHTML(query) + '"' +
        '</p>' +
      '</div>'
    );
  }

  function renderResults({ query, products, suggestions }) {
    let html = '<div class="predictive-search-results__inner" data-search-results>';

    if (suggestions && suggestions.length) {
      html += '<ul class="predictive-search-results__list predictive-search-results__wrapper-queries list-unstyled fgp-algolia-pills" role="listbox">';
      suggestions.forEach((s) => { html += renderSuggestionPill(s); });
      html += '</ul>';
    }

    if (products && products.length) {
      html += '<div id="predictive-search-products" class="predictive-search-results__products">';
      html += '<h4 class="predictive-search-results__title">' + (isArabic() ? 'منتجات' : 'Products') + '</h4>';
      html += '<ul class="predictive-search-results__list predictive-search-results__wrapper-products list-unstyled fgp-algolia-grid" role="listbox">';
      products.forEach((p) => { html += renderProductCard(p); });
      html += '</ul>';
      html += '</div>';
    } else if (query) {
      html += renderEmpty(query);
    }

    html += '</div>';
    return html;
  }

  // ----- Wire up -----

  function findContainer(form) {
    return form.querySelector('.predictive-search-form__content-wrapper--inline')
        || form.querySelector('.predictive-search-form__content-wrapper');
  }

  function findContent(form) {
    return form.querySelector('.predictive-search-form__content');
  }

  function showDropdown(component) {
    const wrap = component.querySelector('.predictive-search-form__content-wrapper--inline')
              || component.querySelector('.predictive-search-form__content-wrapper');
    if (!wrap) return;
    component.classList.add('dropdown-open');
    wrap.style.display = 'flex';
    wrap.style.visibility = 'visible';
    const list = component.querySelector('.predictive-search-dropdown');
    if (list) list.setAttribute('aria-expanded', 'true');
    const input = component.querySelector('.search-input--inline');
    if (input) input.setAttribute('aria-expanded', 'true');
  }

  function hideDropdown(component) {
    const wrap = component.querySelector('.predictive-search-form__content-wrapper--inline')
              || component.querySelector('.predictive-search-form__content-wrapper');
    if (!wrap) return;
    component.classList.remove('dropdown-open');
    wrap.style.display = 'none';
    wrap.style.visibility = 'hidden';
    const list = component.querySelector('.predictive-search-dropdown');
    if (list) list.setAttribute('aria-expanded', 'false');
    const input = component.querySelector('.search-input--inline');
    if (input) input.setAttribute('aria-expanded', 'false');
  }

  async function paint(form, query) {
    const content = findContent(form);
    if (!content) return;

    // Empty input: nothing to show. The homepage's trending collection
    // serves as the discovery surface — keep the search dropdown silent
    // until the user types.
    if (!query || !query.trim()) {
      content.innerHTML = '';
      return;
    }

    // Loading shimmer
    content.innerHTML =
      '<div class="predictive-search-results__inner" data-search-results>' +
        '<div class="fgp-algolia-loading" aria-busy="true">…</div>' +
      '</div>';

    const { products, suggestions } = await fetchSearchResults(query.trim());
    content.innerHTML = renderResults({ query: query.trim(), products, suggestions });
    form.dataset.lastSingleResult = (products.length === 1) ? productUrl(products[0].handle) : '';
  }

  function wireForm(form) {
    if (form.dataset.algoliaWired === '1') return;
    form.dataset.algoliaWired = '1';

    const component = form.closest('predictive-search-component, .predictive-search') || form.parentElement;
    const input = form.querySelector('.search-input--inline');
    if (!input) return;

    const debouncedPaint = debounce(() => paint(form, input.value), CONFIG.debounceMs);

    input.addEventListener('input', () => {
      if (input.value && input.value.trim()) {
        showDropdown(component);
      }
      debouncedPaint();
    });

    input.addEventListener('focus', () => {
      // Only reveal the dropdown once the user has typed something. Empty
      // focus stays silent — the homepage already surfaces trending via its
      // own carousel, no need to duplicate that inside the search dropdown.
      if (input.value && input.value.trim()) {
        paint(form, input.value);
        showDropdown(component);
      }
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const single = form.dataset.lastSingleResult;
        if (single) {
          e.preventDefault();
          window.location.href = single;
        }
        // Otherwise let the form submit to /search?q=...
      } else if (e.key === 'Escape') {
        hideDropdown(component);
        input.blur();
      }
    });

    // Click outside closes the dropdown.
    document.addEventListener('click', (e) => {
      if (!component.contains(e.target)) {
        if (!input.value || !input.value.trim()) hideDropdown(component);
      }
    });
  }

  function init() {
    document.querySelectorAll('[data-algolia-search-form]').forEach(wireForm);
  }

  // Inject minimal CSS for the new layout pieces (cards grid, pills, loading).
  function injectStyles() {
    if (document.getElementById('fgp-algolia-styles')) return;
    const style = document.createElement('style');
    style.id = 'fgp-algolia-styles';
    style.textContent = [
      '.fgp-algolia-pills { display: flex; flex-wrap: wrap; gap: 6px; margin: 0 0 12px; padding: 0; }',
      '.fgp-algolia-pill { display: inline-flex; align-items: center; padding: 4px 10px; border: 1px solid rgb(var(--color-foreground-rgb) / 0.15); border-radius: 999px; font-size: 0.8125rem; text-decoration: none; color: var(--color-foreground); background: rgb(var(--color-foreground-rgb) / 0.04); transition: background 120ms; }',
      '.fgp-algolia-pill:hover { background: rgb(var(--color-foreground-rgb) / 0.08); }',
      '.fgp-algolia-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin: 0; padding: 0; }',
      '@media (min-width: 750px) { .fgp-algolia-grid { grid-template-columns: repeat(4, 1fr); } }',
      '.fgp-algolia-card__link { display: flex; flex-direction: column; gap: 6px; text-decoration: none; color: inherit; padding: 4px; border-radius: 8px; transition: background 120ms; }',
      '.fgp-algolia-card__link:hover { background: rgb(var(--color-foreground-rgb) / 0.04); }',
      '.fgp-algolia-card__image { width: 100%; aspect-ratio: 1; object-fit: contain; background: #fff; border-radius: 6px; display: block; }',
      '.fgp-algolia-card__image--placeholder { background: rgb(var(--color-foreground-rgb) / 0.06); }',
      '.fgp-algolia-card__body { display: flex; flex-direction: column; gap: 2px; }',
      '.fgp-algolia-card__title { font-size: 0.8125rem; font-weight: 500; line-height: 1.25; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }',
      '.fgp-algolia-card__price-row { display: flex; gap: 6px; align-items: baseline; font-size: 0.8125rem; }',
      '.fgp-algolia-card__price { font-weight: 600; color: var(--color-foreground); }',
      '.fgp-algolia-card__compare { color: rgb(var(--color-foreground-rgb) / 0.5); text-decoration: line-through; font-size: 0.75rem; }',
      '.fgp-algolia-loading { padding: 24px; text-align: center; color: rgb(var(--color-foreground-rgb) / 0.5); font-size: 0.875rem; }',
      '.predictive-search-results__title { margin: 0 0 8px; font-size: 0.875rem; font-weight: 500; color: rgb(var(--color-foreground-rgb) / 0.7); }',
      '[dir="rtl"] .fgp-algolia-card__title, [dir="rtl"] .fgp-algolia-card__price-row { text-align: right; }',
    ].join('\n');
    document.head.appendChild(style);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { injectStyles(); init(); }, { once: true });
  } else {
    injectStyles();
    init();
  }
})();
